import { Link } from "react-router-dom";
import { Reveal } from "@/components/landing/Reveal";
import {
  ScanFace, PenTool, FileSearch, Check, Building2, Stamp, User, Car, Gauge, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { icon: Gauge, title: "Simula y crea", desc: "Ingresa patente, RUT y valor. Calcula el costo total al instante." },
  { icon: ScanFace, title: "Valida identidad", desc: "Biometría con prueba de vida y comparación facial certificada." },
  { icon: FileSearch, title: "Carga documentos", desc: "Revisión automática de legibilidad, vigencias y coincidencias." },
  { icon: FileText, title: "Contrato automático", desc: "Motor jurídico genera el contrato de compraventa y anexos." },
  { icon: PenTool, title: "Firma electrónica", desc: "Firma avanzada Ley 19.799 con trazabilidad y certificado." },
  { icon: Gauge, title: "Paga y sigue", desc: "Pago seguro y seguimiento en línea hasta completar el dominio." },
];

const ROLES = [
  { icon: User, title: "Comprador", desc: "Revisa el informe, firma y paga con total seguridad." },
  { icon: Car, title: "Vendedor", desc: "Valida identidad, sube documentos y autoriza la transferencia." },
  { icon: Building2, title: "Automotora", desc: "Carga masiva, sucursales, ejecutivos y reportes tipo Pro." },
  { icon: Stamp, title: "Notaría", desc: "Bandeja de operaciones, evidencia y firma notarial." },
];

const BENCHMARK = [
  "Biometría con prueba de vida integrada en el flujo",
  "Firma electrónica avanzada conforme a la Ley 19.799",
  "Informe Transfiérelo 360 propio con semáforo de riesgo",
  "Motor de riesgo antifraude verde / amarillo / rojo",
  "Cumplimiento Ley 19.628 y Ley 21.719 de datos personales",
  "Paneles dedicados para automotoras y notarías",
];

export const BenchmarkSection = () => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="tf-label mb-3">Por qué Transfiérelo</p>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-[#13315C] leading-tight">
          Lo mejor del mercado, superado con desarrollo propio
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Reunimos las mejores prácticas del rubro y las elevamos con biometría real, certeza jurídica
          y un informe vehicular propio. Todo en un flujo simple, rápido y trazable.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {BENCHMARK.map((b, i) => (
          <Reveal
            key={b}
            delay={i * 0.05}
            className="tf-card p-4 flex items-start gap-3"
          >
            <div className="h-6 w-6 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
              <Check size={14} className="text-[#1F9BD7]" strokeWidth={3} />
            </div>
            <span className="text-sm font-medium text-slate-700">{b}</span>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const StepsSection = () => (
  <section id="como-funciona" className="bg-white border-y border-slate-200 py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <p className="tf-label mb-3">Cómo funciona</p>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-[#13315C]">Seis pasos, cero filas</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {STEPS.map((s, i) => (
          <Reveal
            key={s.title}
            delay={i * 0.06}
            className="tf-card p-6 hover:border-[#13315C]/30 hover:shadow-lg transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="h-11 w-11 rounded-md bg-[#13315C] flex items-center justify-center">
                <s.icon size={20} className="text-white" />
              </div>
              <span className="font-display font-black text-3xl text-[#F5B301]">0{i + 1}</span>
            </div>
            <h3 className="font-display font-bold text-lg text-[#13315C]">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const RolesSection = () => (
  <section className="bg-white border-y border-slate-200 py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <p className="tf-label mb-3">Para todos</p>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-[#13315C]">Una plataforma, todos los actores</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {ROLES.map((r, i) => (
          <Reveal
            key={r.title}
            delay={i * 0.06}
            className="tf-card p-6 text-center hover:shadow-lg transition-all"
          >
            <div className="h-12 w-12 rounded-md bg-[#13315C] flex items-center justify-center mx-auto mb-4">
              <r.icon size={22} className="text-white" />
            </div>
            <h3 className="font-display font-bold text-lg text-[#13315C]">{r.title}</h3>
            <p className="mt-2 text-sm text-slate-600">{r.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const CtaSection = ({ login, user }) => (
  <section className="py-16 md:py-24">
    <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
      <h2 className="font-display font-black text-3xl md:text-5xl text-[#13315C] leading-tight">
        ¿Listo para transferir sin filas?
      </h2>
      <p className="mt-4 text-slate-600 text-lg">Crea tu cuenta y completa tu transferencia digital hoy.</p>
      <Button onClick={user ? undefined : login} asChild={!!user} className="mt-8 bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] rounded-md h-12 px-8 font-semibold text-base" data-testid="cta-bottom-btn">
        {user ? <Link to="/dashboard">Ir a mi panel</Link> : <span>Crear cuenta gratis</span>}
      </Button>
    </div>
  </section>
);
